import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { keyframes } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { useUserRole } from "../../hooks/useUserRole";
import { useGetCompanyByIdQuery } from "../../store/apis/companyApi";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { components } from "../../types/api";
import { buildClaudePrompt } from "../../utils/claudeAutomation/buildClaudePrompt";
import { CompanyAccountField } from "../../utils/claudeAutomation/types";

type AssociatedCampaign = components["schemas"]["AssociatedCampaignResponse"];

/**
 * Naranja de marca de Claude ("Crail"). Es más oscuro que el del icono (#D97757)
 * para que el texto blanco se lea encima; hover y active lo oscurecen un paso más.
 */
const CLAUDE_ORANGE = "#C15F3C";
const CLAUDE_ORANGE_HOVER = "#A9502F";
const CLAUDE_ORANGE_ACTIVE = "#8F4327";

/**
 * Brillo del modal: el naranja del icono (#D97757), más vivo que el del botón.
 * Va con filter: drop-shadow y no con box-shadow porque el tema fuerza
 * `boxShadow: none !important` en todos los Paper, y eso gana incluso a una animación.
 */
const CLAUDE_GLOW = "rgba(217, 119, 87, ";
const GLOW_LOW = `drop-shadow(0 0 10px ${CLAUDE_GLOW}0.55)) drop-shadow(0 0 28px ${CLAUDE_GLOW}0.35))`;
const GLOW_HIGH = `drop-shadow(0 0 16px ${CLAUDE_GLOW}0.9)) drop-shadow(0 0 48px ${CLAUDE_GLOW}0.6))`;

const glowBreathe = keyframes`
  0%, 100% { filter: ${GLOW_LOW}; }
  50% { filter: ${GLOW_HIGH}; }
`;

/** Destello al copiar: el brillo se dispara y el modal da un pequeño latido. */
const GLOW_BURST = `drop-shadow(0 0 22px ${CLAUDE_GLOW}1)) drop-shadow(0 0 80px ${CLAUDE_GLOW}0.85))`;
const COPY_PULSE_FRAMES = `
  0% { transform: scale(1); filter: ${GLOW_LOW}; }
  30% { transform: scale(1.015); filter: ${GLOW_BURST}; }
  100% { transform: scale(1); filter: ${GLOW_LOW}; }
`;
// Dos copias idénticas: alternar el nombre es lo que reinicia la animación en cada copia.
const copyPulseA = keyframes`${COPY_PULSE_FRAMES}`;
const copyPulseB = keyframes`${COPY_PULSE_FRAMES}`;

const ICON_SPIN_FRAMES = `
  0% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.35); }
  100% { transform: rotate(360deg) scale(1); }
`;
const iconSpinA = keyframes`${ICON_SPIN_FRAMES}`;
const iconSpinB = keyframes`${ICON_SPIN_FRAMES}`;

const modalEnter = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;
const CLAUDE_ICON = "/icons/Claude_AI_symbol.svg";

/** Roles que pueden leer la ficha de empresa (GET /companies/:id) sin ser miembros. */
const COMPANY_READER_ROLES = ["ADMIN", "STRATEGY", "PRICING", "ACCOUNT"];

interface AutomateWithClaudeButtonProps {
  associatedCampaign: AssociatedCampaign;
  briefing?: {
    name?: string | null;
    campaignName?: string | null;
    brandOrProduct?: string | null;
    companyName?: string | null;
  } | null;
  companyId?: string;
}

const AutomateWithClaudeButton: React.FC<AutomateWithClaudeButtonProps> = ({
  associatedCampaign,
  briefing,
  companyId,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { role } = useUserRole();
  const [open, setOpen] = useState(false);
  // Cuántas veces se ha copiado en esta apertura: > 0 = "copiado", y su paridad
  // alterna las animaciones para que se repitan en cada clic.
  const [copyCount, setCopyCount] = useState(0);
  const copied = copyCount > 0;
  // Tras copiar, el ratón sigue encima del botón: el hover no vuelve a
  // "Copy prompt" hasta que sale una vez, o nunca se vería "Prompt copied".
  const [recopyOnHover, setRecopyOnHover] = useState(false);
  const copyPulse = copyCount % 2 ? copyPulseA : copyPulseB;
  const iconSpin = copyCount % 2 ? iconSpinA : iconSpinB;

  // Los IDs de cuenta de la empresa solo se piden al abrir y si el rol puede leerlos:
  // para el resto un 403 forzaría un refresco de token inútil, y el prompt ya le dice
  // a Claude que busque la cuenta por el nombre del cliente.
  const canReadCompany = Boolean(role && COMPANY_READER_ROLES.includes(role));
  const { data: companyResponse } = useGetCompanyByIdQuery(companyId ?? "", {
    skip: !open || !companyId || !canReadCompany,
  });

  const { prompt, resolved, missingSections } = useMemo(
    () =>
      buildClaudePrompt({
        associatedCampaign,
        briefing,
        role,
        companyAccounts: companyResponse?.data as
          | Partial<Record<CompanyAccountField, string[]>>
          | null
          | undefined,
      }),
    [associatedCampaign, briefing, role, companyResponse]
  );

  const closeDialog = (): void => {
    setOpen(false);
    setCopyCount(0);
    setRecopyOnHover(false);
  };

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopyCount((count) => count + 1);
      setRecopyOnHover(false);
      dispatch(
        setToastMessage({
          message: t(
            "claudeAutomation.copied",
            "Prompt copied. Paste it into Claude in your browser."
          ),
          severity: "success",
        })
      );
    } catch {
      dispatch(
        setToastMessage({
          message: t(
            "claudeAutomation.copyError",
            "Could not copy the prompt. Select the text and copy it manually."
          ),
          severity: "error",
        })
      );
    }
  };

  const tagSx = {
    height: 18,
    fontSize: 10,
    fontWeight: 700,
    "& .MuiChip-label": { px: 0.75 },
  };

  const steps = [
    t(
      "claudeAutomation.step1",
      "Open Claude in your browser (Claude in Chrome) with {{tool}} open in a tab.",
      { tool: resolved.template.tool }
    ),
    t(
      "claudeAutomation.step2",
      "Copy the prompt below and paste it into Claude."
    ),
    t(
      "claudeAutomation.step3",
      "Follow along: Claude will ask whenever data is missing and will leave everything paused."
    ),
    t(
      "claudeAutomation.step4",
      "When it's done, paste the ID Claude gives you into Source ID (Campaign info → Edit)."
    ),
  ];

  return (
    <>
      <Button
        variant="contained"
        disableElevation
        onClick={() => setOpen(true)}
        startIcon={
          <Box
            component="img"
            src={CLAUDE_ICON}
            alt=""
            // El SVG viene en naranja: en blanco para que se vea sobre el fondo.
            sx={{ width: 20, height: 20, filter: "brightness(0) invert(1)" }}
          />
        }
        endIcon={
          <Stack direction="row" spacing={0.5}>
            <Chip
              label="New!"
              size="small"
              sx={{ ...tagSx, bgcolor: "#FFFFFF", color: CLAUDE_ORANGE }}
            />
            <Chip
              label="Beta"
              size="small"
              variant="outlined"
              sx={{
                ...tagSx,
                borderColor: "rgba(255, 255, 255, 0.8)",
                color: "#FFFFFF",
              }}
            />
          </Stack>
        }
        sx={{
          bgcolor: CLAUDE_ORANGE,
          color: "#FFFFFF",
          fontWeight: 700,
          letterSpacing: 0.3,
          whiteSpace: "nowrap",
          alignSelf: { xs: "flex-start", md: "center" },
          transition: "background-color 0.2s, box-shadow 0.2s",
          "&:hover": {
            bgcolor: CLAUDE_ORANGE_HOVER,
            boxShadow: `0 4px 12px ${CLAUDE_ORANGE}59`,
          },
          "&:active": { bgcolor: CLAUDE_ORANGE_ACTIVE },
          "&.Mui-focusVisible": {
            outline: `2px solid ${CLAUDE_ORANGE}`,
            outlineOffset: 2,
          },
        }}
      >
        {t("claudeAutomation.button", "AUTOMATE WITH CLAUDE")}
      </Button>

      <Dialog
        open={open}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${CLAUDE_GLOW}0.6)`,
            filter: GLOW_LOW,
            // Al cambiar solo el último nombre de la lista, entrada y respiración
            // siguen su curso y el pulso arranca de cero en cada copia.
            animation: [
              `${modalEnter} 0.35s ease-out`,
              `${glowBreathe} 3.2s ease-in-out 0.35s infinite`,
              ...(copied ? [`${copyPulse} 0.7s ease-out`] : []),
            ].join(", "),
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          },
        }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              component="img"
              src={CLAUDE_ICON}
              alt=""
              sx={{
                width: 24,
                height: 24,
                animation: copied ? `${iconSpin} 0.7s ease-in-out` : "none",
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                },
              }}
            />
            <Typography variant="h6" fontWeight={700} component="span">
              {t("claudeAutomation.title", "Automate with Claude")}
            </Typography>
            <Chip
              label="Beta"
              size="small"
              variant="outlined"
              sx={{
                ...tagSx,
                borderColor: CLAUDE_ORANGE,
                color: CLAUDE_ORANGE,
              }}
            />
          </Stack>
          <IconButton
            aria-label={t("common.close", "Close")}
            onClick={closeDialog}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
              {steps.map((step) => (
                <Typography component="li" variant="body2" key={step}>
                  {step}
                </Typography>
              ))}
            </Box>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography variant="body2" color="text.secondary">
                {t("claudeAutomation.template", "Template")}:
              </Typography>
              <Chip
                size="small"
                label={resolved.template.tool}
                sx={{ fontWeight: 600 }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "monospace" }}
              >
                {resolved.template.id}
              </Typography>
            </Stack>

            {missingSections.length > 0 && (
              <Alert severity="warning">
                {t(
                  "claudeAutomation.missingSections",
                  "This line has no {{sections}} yet. The prompt flags the missing data and tells Claude to stop and ask.",
                  {
                    sections: missingSections
                      .map((s) => t(`claudeAutomation.sections.${s}`, s))
                      .join(", "),
                  }
                )}
              </Alert>
            )}

            <Alert severity="info">
              {t(
                "claudeAutomation.disclaimer",
                "Automatically generated prompt (beta). Check what Claude creates before activating anything."
              )}
            </Alert>

            <Box
              component="pre"
              data-testid="claude-prompt"
              sx={{
                m: 0,
                p: 2,
                maxHeight: "45vh",
                overflow: "auto",
                bgcolor: theme.palette.grey[50],
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
                fontFamily: "monospace",
                fontSize: 12,
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {prompt}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDialog}>{t("common.close", "Close")}</Button>
          <Button
            variant="contained"
            onClick={handleCopy}
            onMouseLeave={() => setRecopyOnHover(true)}
            onBlur={() => setRecopyOnHover(true)}
            aria-live="polite"
            sx={{
              bgcolor: CLAUDE_ORANGE,
              minWidth: 170,
              "&:hover": { bgcolor: CLAUDE_ORANGE_HOVER },
              "&:active": { bgcolor: CLAUDE_ORANGE_ACTIVE },
              // Ya copiado: muestra "Prompt copied"; al pasar por encima vuelve a
              // ofrecer "Copy prompt" para volver a copiarlo.
              "& .copy-label": { display: copied ? "none" : "inline-flex" },
              "& .copied-label": { display: copied ? "inline-flex" : "none" },
              ...(recopyOnHover && {
                "&:hover, &.Mui-focusVisible": {
                  "& .copy-label": { display: "inline-flex" },
                  "& .copied-label": { display: "none" },
                },
              }),
            }}
          >
            <Box
              component="span"
              className="copy-label"
              sx={{ alignItems: "center", gap: 1 }}
            >
              <ContentCopyIcon fontSize="small" />
              {t("claudeAutomation.copy", "Copy prompt")}
            </Box>
            <Box
              component="span"
              className="copied-label"
              sx={{ alignItems: "center", gap: 1 }}
            >
              <CheckIcon fontSize="small" />
              {t("claudeAutomation.copiedButton", "Prompt copied")}
            </Box>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AutomateWithClaudeButton;
